import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import Markdown from 'react-markdown';
import { ArrowLeft, ChevronRight, ChevronDown, Send, Reply, Megaphone, Inbox, Play, Pause, RefreshCw, RotateCcw, KeyRound, Power, Info, FileCode, Eraser, Pencil, Server, Square, Volume2, Copy, Check, MessagesSquare, Plus, Bookmark, Rewind, AudioLines, Download, Mic, MicOff, Pin, Compass, X, ListChecks, Bot } from 'lucide-react';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import {
  isStreamFilterKey, jumpDirectionFor, nextOwnTurnIndex, nextStreamFilter,
  turnVisibleInFilter, STREAM_FILTER_LABEL, type JumpDirection, type StreamFilter,
} from '../lib/ownTurnJump';
import { MicLevelMeter } from './MicLevelMeter';
import { AudioInputPicker } from './AudioInputPicker';
import { VoiceStartDialog } from './VoiceStartDialog';
import { AgentIssues } from './AgentIssues';
import { useOlderHistory } from '../hooks/useConversation';
import { useTags } from '../hooks/useTags';
import { TagPanel } from './TagPanel';
import { contentApi, type CollabInboxPost } from '../api/contentApi';
import type { V2Response, V2Turn, V2Section, AgentState, SessionInfo } from '../api/types';
import { api, OPEN_ISSUE_STATUSES } from '../api/client';
import { formatLoginAge, formatVerdict, formatExpiredSince, liveAges, type TokenStats } from '../lib/tokenAge';
import { useNow, computeNudgeEta, formatMmSs } from '../hooks/useNow';
import { InputBar, type InputBarHandle } from './InputBar';
import { MultiChoiceBlock } from './MultiChoiceBlock';
import { PermissionBlock } from './PermissionBlock';
import { SuggestionBar } from './SuggestionBar';
import { StepProgressBar } from './StepProgressBar';
import { AgentInfoModal } from './AgentInfoModal';
import { McpPanel } from './McpPanel';
import { PromptInspectorModal } from './PromptInspectorModal';
import { ResetModal } from './ResetModal';
import { AgentLoginModal } from './AgentLoginModal';
import { CloneDialog } from './CloneDialog';
import { MoveDialog } from './MoveDialog';
import { AgentOsWave } from './AgentOsWave';
import { AgentHoverTooltip, HOVER_CAPABLE } from './AgentHoverTooltip';
import { useVoice } from '../contexts/VoiceContext';
import { useInstanceMode } from '../hooks/useInstanceMode';
import { useServiceAvailable } from '../hooks/useServiceOrigin';
import { serviceAvailable } from '../lib/services';
import { turnNarrationText } from '../lib/narration';
// The Markdown rendering pipeline (mdComponents + plugins + media/embed
// helpers) lives in richMarkdown so the collab bubbles share it 1:1
// (Alex 2026-06-20). AgentDetail keeps its own <Markdown> call-sites but
// pulls the pieces from there — one source of truth for mdComponents.
import {
  mdComponents,
  REMARK_PLUGINS,
  REHYPE_PLUGINS,
  stripAnsi,
  STORAGE_MEDIA,
  storageMediaUrl,
  IMAGE_EXTS,
  AUDIO_EXTS,
} from './richMarkdown';
import './AgentDetail.css';


interface Props {
  sessionName: string;
  /** Full SessionInfo from the federation list. Needed for the Info modal —
   *  carries owner_email, node, department, model, etc. that aren't in the
   *  V2 response. Null while the session list hasn't loaded yet. */
  session?: SessionInfo | null;
  data: V2Response | null;
  agentState?: AgentState;
  onBack?: () => void;
  onSelectSession?: (name: string) => void;
  /** Opens the Edit-Agent dialog (App-owned). When present the header
   *  menu offers "Edit Agent" so it matches the sidebar context menu. */
  onEditAgent?: (session: SessionInfo) => void;
  /** Operator-only manual CLI/LLM handoff for an existing agent (#562). */
  onSwitchAgent?: (session: SessionInfo) => void;
  /** Open the group-question dialog with this agent pre-selected (the "+"
   *  in the input row, Alex 2026-06-20). */
  onAskGroup?: (agentName: string) => void;
  /** Open an existing group chat by post id — from the header dropdown that
   *  lists the group chats this agent is in (Alex 2026-06-22). */
  onOpenCollab?: (postId: number) => void;
}

function stateLabel(state: string): string {
  switch (state) {
    case 'ready': return 'READY';
    case 'thinking': return 'THINKING';
    case 'tool': return 'TOOL';
    case 'error': return 'ERROR';
    case 'dead': return 'DEAD';
    case 'suspended': return 'SUSPENDED';
    case 'interrupted': return 'INTERRUPTED';
    case 'rate_limited_transient': return 'RATE-LIMITED';
    case 'waiting': return 'WARTET';
    // Terminal blocking states (Cloud IACP fb6dd9b2) — previously fell to
    // 'IDLE', so a revoked-token agent read as idle in the header badge.
    case 'needs_login': return 'LOGIN NÖTIG';
    case 'out_of_credits': return 'CREDITS LEER';
    case 'quota_exhausted': return 'KONTINGENT LEER';
    default: return 'IDLE';
  }
}

function stateBadgeClass(state: string): string {
  switch (state) {
    case 'ready': return 'badge-success';
    // Working = accent, matching the rest of the active indicators (status
    // dot / wave / elapsed). interrupted/rate-limit/waiting keep warning —
    // those are attention states, not "actively working" (Alex 2026-06-23).
    case 'thinking':
    case 'tool': return 'badge-working';
    case 'error': return 'badge-error';
    // Auth/credits are hard blocks — red like error, they need action.
    case 'needs_login':
    case 'out_of_credits': return 'badge-error';
    case 'quota_exhausted': return 'badge-error';
    // Suspended is a legitimate paused state — neutral grey, not warning.
    case 'suspended': return 'badge-neutral';
    // Interrupted is a user-initiated stop — not an error, but not a
    // success either. Mirror the rate-limit accent so the user sees
    // immediate "the agent stopped, your action landed" feedback
    // (Issue #57). Auto-clears on next pane change.
    case 'interrupted':
    case 'rate_limited_transient': return 'badge-warning';
    // Waiting on a permission prompt — needs the user's attention.
    case 'waiting': return 'badge-warning';
    default: return '';
  }
}

function statusDotClass(state: string): string {
  switch (state) {
    case 'ready': return 'ready';
    case 'thinking':
    case 'tool': return 'thinking';
    case 'error': return 'error';
    case 'needs_login':
    case 'out_of_credits': return 'error';
    case 'quota_exhausted': return 'error';
    case 'suspended': return 'suspended';
    case 'interrupted': return 'interrupted';
    case 'rate_limited_transient': return 'thinking';
    case 'waiting': return 'waiting';
    default: return 'idle';
  }
}

import { TurnEntry, parseTurnMeta } from './AgentStream';

export function AgentDetail({ sessionName, session, data, agentState, onBack, onSelectSession, onEditAgent, onSwitchAgent, onAskGroup, onOpenCollab }: Props) {
  const streamRef = useRef<HTMLDivElement>(null);
  const nearBottom = useRef(true);
  const [sending, setSending] = useState(false);
  // Bridge the poll-lag gap between "send POST finished" and "agent state
  // shows thinking" so the stop/wave indicator doesn't flicker off then back on
  // (Alex 2026-06-22). Held from send-start until the agent's turn ends.
  const [busyHold, setBusyHold] = useState(false);
  // Back-pressure queue depth for this agent (#1257 Phase B) — "X wartend" in the
  // header. Counted from the issues-by-assignee list (which the user JWT can read) — the
  // dedicated /dispatch/queue endpoint returns 401 for the browser bearer (Alex's console
  // error), so we avoid it and derive the count from issues with dispatch_status=queued.
  const [queueCount, setQueueCount] = useState(0);
  // Unprocessed (open) issues assigned to this agent — colours the header Issues button
  // blue so Alex sees at a glance there's something waiting (#177). Same OPEN set as the
  // AgentIssues panel (open/in_progress/deferred).
  const [openIssueCount, setOpenIssueCount] = useState(0);
  // Alle 8 Sekunden, mit einem ROHEN Intervall — also auch in einem verdeckten
  // Tab. Und `forAssignee` ist kein billiger Aufruf: Es blaettert je offenem
  // Status durch alle Vorgaenge des Agenten, drei Abfragen pro Runde, nur um
  // zwei Zahlen zu bilden.
  //
  // Cloud hat es gemessen: 74 Listenabrufe je Minute, davon Dreierbloecke der
  // Form `assignee=agent:X&status=…`. Das ist diese Stelle. `useLaneStatus`
  // wurde genau deswegen schon einmal von einem rohen Intervall befreit —
  // diese hier ist dabei uebersehen worden.
  //
  // 60 s statt 8 s, und sichtbarkeitsgebunden: Ein Abzeichen fuer wartende
  // Vorgaenge veraltet nicht in Sekunden, und ein Tab im Hintergrund braucht
  // es gar nicht. Beim Zurueckkommen wird sofort neu geladen.
  const loadIssueCounts = useCallback(() => {
    if (!serviceAvailable('issue')) return;
    void api.issues.countsFor(`agent:${sessionName}`)
      .then((c) => {
        setQueueCount(c.queued || 0);
        setOpenIssueCount(OPEN_ISSUE_STATUSES.reduce((sum, st) => sum + (c[st] || 0), 0));
      })
      .catch(() => { setQueueCount(0); setOpenIssueCount(0); });
  }, [sessionName]);
  useVisiblePolling(loadIssueCounts, 60000);
  // SPRUNG ZUR EIGENEN VORIGEN NACHRICHT (Alex, 12.08.2026)
  //
  // „Wenn ich meine eigenen Nachrichten suche, dann finde ich diese nicht."
  // Ein einzelner Agentenzug füllt hier den halben Bildschirm, dazwischen
  // stehen fremde IACP-Blöcke — die eigene letzte Zeile ist nicht mehr
  // auffindbar. Strg+Pfeil hoch springt zur vorigen eigenen Nachricht, noch
  // einmal zur darüber; Pfeil runter zurück.
  //
  // Die Auswahl steckt in ownTurnJump.ts, hier nur der Griff ins DOM: Die
  // eigenen Züge tragen `data-own` (gesetzt von TurnEntry aus derselben
  // Sender-Erkennung, die auch die Anzeige benutzt).
  // Sichtfilter des Verlaufs (Alex, 12.08.): Alles → nur meine Eingaben →
  // meine Eingaben + IACP → wieder alles. Ausgeblendet werden die ANTWORTEN,
  // nicht die Züge — gesucht wird ja die Eingabe, nicht ihr Umfeld.
  const [streamFilter, setStreamFilter] = useState<StreamFilter>('all');

  const jumpToOwnTurn = useCallback((direction: JumpDirection) => {
    const el = streamRef.current;
    if (!el) return;
    const own = Array.from(el.querySelectorAll<HTMLElement>('.stream-entry[data-own="1"]'));
    if (!own.length) return;
    // NICHT `offsetTop`: Das zählt ab dem nächsten positionierten Vorfahren,
    // und der ist hier nicht der Scroll-Behälter. Die Werte sind dadurch um
    // einen festen Betrag zu groß — der Sprung landete genau darunter, sodass
    // Alex' eigene Zeile oben aus dem Bild lief und nur noch „Gesendet · 15:23"
    // zu sehen war (gemeldet 12.08. mit zwei Bildschirmfotos).
    //
    // `getBoundingClientRect` misst gegen den Behälter selbst, unabhängig
    // davon, wo der Bezugspunkt liegt.
    const containerTop = el.getBoundingClientRect().top;
    const offsets = own.map((node) => node.getBoundingClientRect().top - containerTop + el.scrollTop);
    const index = nextOwnTurnIndex(offsets, el.scrollTop, direction);
    if (index == null) return;
    // Ein Rand von 12px, damit die Zeile nicht bündig an der Oberkante klebt.
    el.scrollTop = Math.max(0, offsets[index] - 12);
    const target = own[index];
    // Ohne sichtbares Ziel wirkt der Sprung wie ein zufälliger Scroll: Man
    // landet irgendwo und muss selbst suchen, was gemeint war.
    target.classList.remove('own-jump-hit');
    void target.offsetWidth;
    target.classList.add('own-jump-hit');
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isStreamFilterKey(event)) {
        event.preventDefault();
        setStreamFilter((prev) => nextStreamFilter(prev));
        return;
      }
      const direction = jumpDirectionFor(event);
      if (!direction) return;
      // Auch aus dem Eingabefeld heraus: Strg/Alt+Pfeil bewegt dort keinen
      // Cursor, den man vermissen würde, und genau dort steht man, wenn man
      // nachsehen will, was man zuletzt geschrieben hat.
      event.preventDefault();
      jumpToOwnTurn(direction);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [jumpToOwnTurn]);

  // Optimistic STOP: ESC in Claude Code stops immediately, but our backend
  // `state` lags ~1 scrape (~2 s), so the stop button + wave lingered after a
  // click (Alex 2026-06-23: "stop ist stop"). This flag hides the indicator
  // the instant Stop is pressed; a safety timeout un-hides it if the agent is
  // somehow still working (ESC didn't land) so it stays stoppable.
  const [stopped, setStopped] = useState(false);
  const handleSending = useCallback((s: boolean) => {
    setSending(s);
    if (s) { setBusyHold(true); setStopped(false); }
  }, []);
  const [dragging, setDragging] = useState(false);
  const [mcpCount, setMcpCount] = useState(0);
  const turnCountRef = useRef(data?.turn_count || 0);
  const dragCounter = useRef(0);

  // Stop the agent — double Escape to the pane (mirrors InputBar's old
  // stopAgent; the button now lives next to the working wave below).
  const stopAgent = useCallback(() => {
    // Hide the stop button + wave immediately — feel like ESC.
    setStopped(true);
    setBusyHold(false);
    setSending(false);
    api.sessions.sendKey(sessionName, 'Escape');
    setTimeout(() => api.sessions.sendKey(sessionName, 'Escape'), 150);
    // The normal "agent stopped" path is the busy→idle transition below, which
    // clears `stopped` once the backend confirms idle (~2–4 s). This timeout is
    // only the last-resort fallback for a genuine non-stop (ESC didn't land and
    // the agent is STILL working) — generous so a normal state-lag never makes
    // the button flash back (Alex 2026-06-23).
    setTimeout(() => setStopped(false), 10000);
  }, [sessionName]);
  const inputBarRef = useRef<InputBarHandle>(null);

  // Glassmorphism layout (Alex 2026-07-03): header + footer are translucent blur
  // OVERLAYS and the transcript scrolls full-height beneath them. Their heights are
  // dynamic (footer grows with textarea/attachments; header expands) — a
  // ResizeObserver mirrors them into CSS vars the stream uses as padding.
  const glassContainerRef = useRef<HTMLDivElement>(null);
  const glassHeaderRef = useRef<HTMLElement>(null);
  const glassFooterRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const c = glassContainerRef.current;
    if (!c) return;
    const update = () => {
      c.style.setProperty('--agent-hdr-h', `${glassHeaderRef.current?.offsetHeight ?? 0}px`);
      c.style.setProperty('--agent-ftr-h', `${glassFooterRef.current?.offsetHeight ?? 0}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    if (glassHeaderRef.current) ro.observe(glassHeaderRef.current);
    if (glassFooterRef.current) ro.observe(glassFooterRef.current);
    return () => ro.disconnect();
  }, []);

  // Lifecycle quick-actions menu opened by clicking the agent name in the
  // header — mirrors the right-click context menu in SessionList so users
  // don't have to swap into the sidebar to suspend/restart/kill the agent
  // they're already looking at. Edit Agent + MCP Server now also live here
  // (shared <McpPanel> + onEditAgent prop) so the header menu matches the
  // sidebar context menu exactly — Alex flagged the divergence 2026-06-13.
  const [headerMenu, setHeaderMenu] = useState<{ x: number; y: number } | null>(null);
  // Clicking the header BACKGROUND expands it (not the name → that's the
  // context menu). The expanded panel lists the group chats this agent is in
  // + a quick filter + "new" (Alex 2026-06-22).
  const [headerExpanded, setHeaderExpanded] = useState(false);
  const [issuePanelOpen, setIssuePanelOpen] = useState(false);
  const [groupChats, setGroupChats] = useState<CollabInboxPost[]>([]);
  const [groupChatsError, setGroupChatsError] = useState(false);
  const [gcFilter, setGcFilter] = useState('');
  // History-tags (Cloud 2026-06-23) — bookmark + rewind panel.
  const [tagPanelOpen, setTagPanelOpen] = useState(false);
  const tagsApi = useTags(sessionName);
  const pinnedTag = session?.pinned_resume_tag || null;
  // Persistent AgentOS voice narrator — lives app-level (VoiceContext), survives
  // navigation; this view drives feed + focus for the agent it shows (#1215).
  const voice = useVoice();
  const voiceLive = voice.state === 'live' || voice.state === 'connecting';
  // Pre-start config dialog (Alex 2026-06-26): pick mode/detail before Start.
  const [voiceStartOpen, setVoiceStartOpen] = useState(false);
  const lastFedRef = useRef('');
  const lastFedUserRef = useRef('');
  const primedRef = useRef(false);
  // Continuous-flow focus (Alex' directive): tell the provider this is the home
  // agent. The provider owns focus — it shifts the live narrator here (and away
  // for ambient roam) without teardown. setBaseFocus no-ops if unchanged; on a
  // real switch it cancels roaming + re-anchors. AgentDetail is reused across
  // switches (refs persist), so clear the feed baseline so priming + delta
  // effects re-run for the new home agent.
  const lastBaseRef = useRef<string | null>(null);
  useEffect(() => {
    if (voice.state !== 'live') { lastBaseRef.current = null; return; }
    voice.setBaseFocus(sessionName);
    // Re-prime ONLY on a real home-agent switch (not on every render) — the
    // refs persist across switches since AgentDetail is reused.
    if (lastBaseRef.current !== null && lastBaseRef.current !== sessionName) {
      primedRef.current = false;
      lastFedRef.current = '';
      lastFedUserRef.current = '';
    }
    lastBaseRef.current = sessionName;
  }, [voice, sessionName]);
  const [voiceLogOpen, setVoiceLogOpen] = useState(false);
  const [voiceReportHidden, setVoiceReportHidden] = useState(false);
  const lastVoiceLine = [...voice.transcript].reverse().find((l) => l.role === 'voice');

  // Export the full session log (feed ↔ narration ↔ operator, with timestamps +
  // segment/epoch meta) as JSON for offline finetuning analysis (Alex 2026-06-24).
  const downloadVoiceLog = useCallback(() => {
    const payload = {
      session_id: voice.meta.id,
      agent: sessionName,
      started_at: voice.meta.startedAt ? new Date(voice.meta.startedAt).toISOString() : null,
      ended_at: voice.meta.endedAt ? new Date(voice.meta.endedAt).toISOString() : null,
      usage: voice.usage,
      lines: voice.transcript.map((l) => ({
        ts: l.ts ? new Date(l.ts).toISOString() : null,
        role: l.role, kind: l.kind, agent: l.agent, epoch: l.epoch, seg: l.seg, text: l.text,
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `voice-log-${voice.meta.id || sessionName}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }, [voice.meta, voice.usage, voice.transcript, sessionName]);

  // Persist each finished session so it survives reload + is re-downloadable
  // (keep the last 10, capped, in localStorage). Best-effort; quota-safe.
  useEffect(() => {
    if (voice.state !== 'stopped' || !voice.meta.id || voice.transcript.length === 0) return;
    try {
      const KEY = 'cloud-voice-logs';
      const prev = JSON.parse(localStorage.getItem(KEY) || '[]');
      const entry = {
        session_id: voice.meta.id, agent: sessionName,
        started_at: voice.meta.startedAt, ended_at: voice.meta.endedAt,
        lines: voice.transcript,
      };
      const next = [entry, ...prev.filter((e: { session_id?: string }) => e.session_id !== voice.meta.id)].slice(0, 10);
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch { /* quota / serialization — skip, the download button still works */ }
  }, [voice.state, voice.meta, voice.transcript, sessionName]);
  // Collapse per-agent panels on switch (#233: the login modal + issue/tag panels
  // are AgentDetail-instance state; without this the login modal stayed open and
  // "followed" onto every agent you switched to — Alex saw 'Login manuell' everywhere).
  useEffect(() => { setHeaderExpanded(false); setTagPanelOpen(false); setShowLogin(false); setIssuePanelOpen(false); }, [sessionName]);
  useEffect(() => {
    if (!headerExpanded) return;
    let cancelled = false;
    contentApi.collabInbox(sessionName)
      .then((r) => { if (!cancelled) { setGroupChats(r.posts || []); setGroupChatsError(false); } })
      // Surface the failure (#231: a 500 looked like "keine Gruppenchats") — an
      // empty list and a broken endpoint are different truths.
      .catch(() => { if (!cancelled) setGroupChatsError(true); });
    return () => { cancelled = true; };
  }, [headerExpanded, sessionName]);
  const [showInfo, setShowInfo] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [showClone, setShowClone] = useState(false);
  const [showMove, setShowMove] = useState(false);
  // Standalone-Instanz: kein zweiter Knoten, kein Umzugsziel (#4701).
  const instanceMode = useInstanceMode();
  const issueAvailable = useServiceAvailable('issue');
  const storageAvailable = useServiceAvailable('storage');
  const [showMcp, setShowMcp] = useState(false);
  const [showLogin, setShowLogin] = useState<false | 'both' | 'manual'>(false);
  // SessionList's context menu can request the login modal cross-component (#228).
  useEffect(() => {
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent<{ session?: string; mode?: 'both' | 'manual' }>).detail;
      if (d?.session === sessionName) setShowLogin(d.mode || 'both');
    };
    window.addEventListener('cloud-open-login', onOpen);
    return () => window.removeEventListener('cloud-open-login', onOpen);
  }, [sessionName]);
  // Desktop hover info card on the header name (model / node / size).
  const [nameHoverRect, setNameHoverRect] = useState<DOMRect | null>(null);
  const nameHoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [headerStatus, setHeaderStatus] = useState('');

  // Auto-wake a non-live agent on open (Alex 2026-06-23): a click should bring
  // it up. `POST /wake` runs Cloud's `ensure_live` state-machine — ONE call for
  // every paused state (suspended → resume --continue, orphan → register +
  // tmux cold-start), federation-aware (forwards to the owning node, e.g.
  // Sabrina on arkserver). `.no_autostart` → `skipped_no_autostart` = disabled.
  const [wakeState, setWakeState] = useState<'idle' | 'waking' | 'blocked' | 'error'>('idle');
  const [wakeMsg, setWakeMsg] = useState('');
  const wakedRef = useRef<string | null>(null);
  // Any lifecycle that isn't clearly in-memory needs a wake. Mirrors
  // SessionList's `isPaused`. /wake is a no-op for an already-live agent.
  const wakeLifecycle = session?.status || (session?.actually_running === false ? 'unknown' : 'running');
  const needsWake = wakeLifecycle !== 'running' && wakeLifecycle !== 'registered';
  useEffect(() => {
    wakedRef.current = null;
    setWakeState('idle');
    setWakeMsg('');
    setStopped(false);
  }, [sessionName]);
  useEffect(() => {
    if (!needsWake || !sessionName) return;
    if (wakedRef.current === sessionName) return; // already attempted this open
    wakedRef.current = sessionName;
    setWakeState('waking');
    let cancelled = false;
    api.sessions.wake(sessionName)
      .then((res) => {
        if (cancelled) return;
        if (res?.actions?.[0] === 'skipped_no_autostart') {
          setWakeState('blocked');
          setWakeMsg('Dieser Agent ist bewusst stillgelegt (.no_autostart).');
        } else {
          // Agent is now live; SSE (session.state / pane_dirty) repopulates
          // the conversation. Drop the overlay.
          setWakeState('idle');
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setWakeState('error');
        setWakeMsg(e instanceof Error ? e.message : 'Cold-Start fehlgeschlagen');
      });
    return () => { cancelled = true; };
  }, [sessionName, needsWake]);

  const retryWake = useCallback(() => {
    wakedRef.current = null;
    setWakeState('idle');
  }, []);

  const runHeaderAction = useCallback(async (action: string) => {
    setHeaderMenu(null);
    setHeaderStatus(`${action}…`);
    try {
      if (action === 'resume') await api.sessions.resume(sessionName);
      else if (action === 'suspend') await api.sessions.suspend(sessionName);
      else if (action === 'restart') await api.sessions.restart(sessionName);
      else if (action === 'hard-reset') {
        // Hard Reset = wipe conversation history, fresh start, Persona/MCPs preserved.
        // Distinct from Soft Reset (ResetModal) which sends a /memory-update
        // instruction and continues the conversation. Cloud's IACP 49f6ac81.
        if (!window.confirm(`Hard Reset agent "${sessionName}"?\n\nWipes conversation history (no memory update) and restarts fresh.\nPersona, MCPs and Tools are preserved.\n\nFor a memory-aware reset that keeps the conversation context, use Soft Reset instead.`)) {
          setHeaderStatus('');
          return;
        }
        await api.sessions.restart(sessionName, { wipe_history: true });
      }
      else if (action === 'kill') {
        if (!window.confirm(`Kill agent "${sessionName}"? This terminates the session.`)) {
          setHeaderStatus('');
          return;
        }
        await api.sessions.kill(sessionName);
      }
      else if (action === 'fix-mcp') {
        await fetch(`/api/sessions/${sessionName}/fix-mcp`, { method: 'POST' });
      }
      else if (action === 'login') {
        // Open the login modal: manual path always available + prominent (Login
        // design-sync, #1276) — replaces the old blocking auto-only login-request.
        setShowLogin('both');
        return;
      }
      else if (action === 'login-manual') {
        // Straight to manual (#228) — URL only, no auto-flow, no waiting.
        setShowLogin('manual');
        return;
      }
      setHeaderStatus(`${action}: done`);
    } catch {
      setHeaderStatus(`${action}: failed`);
    }
    setTimeout(() => setHeaderStatus(''), 2000);
  }, [sessionName]);

  useEffect(() => {
    api.sessions.getMcp(sessionName)
      .then((d) => setMcpCount(Object.keys(d.servers).length))
      .catch(() => setMcpCount(0));
  }, [sessionName]);

  // Transcript-backed OLDER history (#1204, Alex 2026-06-23): the live /v2
  // render stays the primary/correct source; history is only prepended ABOVE
  // it for unbounded scroll-back. `liveDropCount` = leading live turns whose
  // (often scrape-truncated) content history already covers completely.
  const liveTurns = useMemo(() => data?.turns ?? [], [data]);
  // liveLoaded: /v2 settled — lets the hook distinguish "empty pane" (restart,
  // transcript-only session) from "still loading" for the empty-pane fallback.
  const hist = useOlderHistory(sessionName, liveTurns, data !== null);

  // Reset the feed-dedup baseline whenever a voice session (re)starts.
  useEffect(() => {
    if (voice.state === 'connecting') { lastFedRef.current = ''; lastFedUserRef.current = ''; primedRef.current = false; setVoiceReportHidden(false); }
  }, [voice.state]);
  // PRIMING (Alex 2026-06-24): when the session goes live, give the narrator a
  // grounded starting point BEFORE any new delta arrives — who it's watching
  // (agent name/node/model/role) + the recent activity so far. Without this, if
  // the agent isn't actively producing at start, the narrator gets nothing and
  // invents. Sets lastFedRef so the delta-feed continues without re-sending.
  useEffect(() => {
    if (voice.state !== 'live' || primedRef.current) return;
    primedRef.current = true;
    const model = data?.metrics?.model || session?.model || '';
    const node = session?.node && session.node !== 'local' ? session.node : 'mac';
    // A touch more depth than just the last turn (Alex 2026-06-24: "ein bisschen
    // mehr Tiefe + Kontext"). Last ~3 turns of real activity.
    const recent = liveTurns.slice(-3).map((t) => turnNarrationText(t)).filter(Boolean).join('\n\n');
    const last = liveTurns[liveTurns.length - 1];
    const lastText = last ? turnNarrationText(last, { inFlight: true }) : '';
    const who = `„${sessionName}"` + (model ? `, ${model}` : '') + `, auf ${node}`
      + (session?.description ? ` (${session.description})` : '');
    // FEED = clean labelled DATA only — NO narration instructions (those leaked
    // into the spoken output: "der letzte Stand war", "jetzt warte ich auf
    // Output" — Alex 2026-06-24). The HOW lives in AiApi's system prompt; the
    // `[historischer Kontext]` label tells it this is past, not live.
    const setup = recent
      ? `[historischer Kontext · Agent ${who}]\n${recent}`
      : `[kein Stream · Agent ${who}] (noch kein sichtbarer Stand)`;
    voice.feedHistory(setup, sessionName);
    lastFedRef.current = lastText;
  }, [voice, liveTurns, session, sessionName, data]);
  // While the narrator is live, feed only the NEWLY-APPENDED text of the focused
  // agent's latest turn — not the whole growing turn (Codex fix 1). Runs only
  // AFTER priming so we don't double-send the latest turn.
  useEffect(() => {
    if (voice.state !== 'live' || !primedRef.current) return;
    const last = liveTurns[liveTurns.length - 1];
    if (!last) return;
    // Feed the operator's typed request TO the agent (turn.user) when it's new,
    // so the narrator knows WHAT Alex asked, not just the reply (Alex 2026-06-24).
    const userMsg = (last.user || '').trim();
    if (userMsg && userMsg !== lastFedUserRef.current) {
      lastFedUserRef.current = userMsg;
      // Don't echo the operator's OWN voice-relayed message back to the narrator —
      // Alex just spoke it via talkback and heard "✓ gesendet", so narrating
      // "CloudV2 received your message" is redundant (Alex 2026-06-24, log
      // vs_CloudV2_154769). The [VOICE RELAY …] prefix + IACP-PROTOCOL boilerplate
      // are also pure noise for the narrator.
      // In transparent mode the model RELAYS fed operator_requests to the agent — so
      // feeding the operator's TYPED input would relay it as if spoken (Alex 2026-06-26:
      // "mein Input ist nicht gesprochen sondern eingegeben", confirmed in vs_CloudV2_
      // 123465: every typed message got `[Relay → CloudV2]`). Typed messages already
      // reach the agent via the normal chat; ONLY live voice should drive a relay.
      if (!userMsg.startsWith('[VOICE RELAY') && !voice.agentTransparent) {
        voice.feedOperatorRequest(userMsg, sessionName);
      }
    }
    // Feed the agent's REAL activity (tools/diffs/todos/recap), not just prose —
    // otherwise the narrator hallucinates (Alex 2026-06-23).
    // inFlight: Der letzte Zug waechst noch. Ein Konnektor-Kandidat an seinem
    // Ende bleibt zurueckgehalten, bis der naechste Block ihn einordnet —
    // sonst schrumpft der Text nachtraeglich und das Praefix-Delta bricht.
    const full = turnNarrationText(last, { inFlight: true });
    if (!full || full === lastFedRef.current) return;
    const prev = lastFedRef.current;
    const delta = (prev && full.startsWith(prev)) ? full.slice(prev.length) : full;
    lastFedRef.current = full;
    if (delta.trim()) voice.feedAgentMessage(delta, sessionName);
  }, [voice, liveTurns, sessionName]);
  const displayTurns = useMemo(
    () => [...hist.older, ...hist.liveWithTiming.slice(hist.liveDropCount)],
    [hist.older, hist.liveDropCount, hist.liveWithTiming],
  );


  // Bookmark the latest SETTLED turn (live /v2 turns carry no uuid yet — only
  // turns written to the transcript do, so we fetch the newest /history page
  // and tag its last turn). This is the "Lesezeichen hier setzen" primitive.
  const bookmarkNow = useCallback(async () => {
    // Prefill a time-based default so it's a one-tap "bam, merk dir das" — type
    // a name to remember it by, or just confirm (Alex 2026-06-23).
    const dflt = `Stand ${new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
    const name = window.prompt('Lesezeichen-Name:', dflt);
    if (!name || !name.trim()) return;
    try {
      const r = await api.sessions.history(sessionName, null, 1);
      const newest = r.turns?.[r.turns.length - 1];
      if (!newest?.uuid) {
        window.alert('Noch kein gespeicherter Gesprächspunkt zum Markieren vorhanden.');
        return;
      }
      await tagsApi.addTag(name.trim(), newest.uuid);
    } catch {
      window.alert('Lesezeichen konnte nicht gesetzt werden.');
    }
  }, [sessionName, tagsApi]);

  // Rewind the agent to a tag — non-destructive (Cloud branches the .jsonl +
  // restarts on the branch). Pin state flips via SSE; /history + /v2 re-resolve
  // on the restart, so we just confirm + fire and close the panel.
  const doResume = useCallback(async (tag: string) => {
    const ok = window.confirm(
      `Agent auf „${tag}" zurückspulen?\n\nDer Verlauf nach diesem Punkt wird in einen Branch verschoben (NICHT gelöscht), der Agent startet ab hier neu. „Zur vollen History" stellt alles wieder her.`,
    );
    if (!ok) return;
    const done = await tagsApi.resumeAtTag(tag);
    if (done) setTagPanelOpen(false);
    else window.alert('Zurückspulen fehlgeschlagen.');
  }, [tagsApi]);

  const doClearResume = useCallback(async () => {
    const done = await tagsApi.clearResume();
    if (!done) window.alert('Zurücksetzen auf volle History fehlgeschlagen.');
  }, [tagsApi]);

  // Latest history controls in a ref so the (stable) scroll handler reads
  // fresh values without re-binding onScroll.
  const histRef = useRef(hist);
  histRef.current = hist;
  // scrollHeight captured the moment a scroll-up load is triggered, so the
  // layout effect can restore the viewport after older turns prepend.
  const prependAnchor = useRef<number | null>(null);
  // Fresh-agent scroll reset (Alex 2026-07-04: landete trotz Pins mitten in der
  // History). Root: ein DANGLING prependAnchor — gesetzt bei einem Nahe-oben-Scroll,
  // aber nur geräumt wenn wirklich ältere Turns ankommen (loadOlder ohne Ergebnis =
  // Anker bleibt für immer, überlebt als Ref sogar Agenten-Wechsel) → entwaffnete
  // ALLE Pins und verrechnete beim nächsten Agenten eine stale Höhe in scrollTop.
  // Dazu ein Switch-Zeitstempel: in den ersten 1.5s nach dem Wechsel ist "unten"
  // autoritativ — programmatische Layout-Sprünge in der Settle-Phase werden weder
  // als User-Scroll gewertet noch dürfen sie nearBottom kippen.
  const switchTsRef = useRef(0);
  useEffect(() => {
    prependAnchor.current = null;
    switchTsRef.current = performance.now();
  }, [sessionName]);
  // Persistent bottom-pin: while nearBottom (user hasn't deliberately scrolled up),
  // EVERY content-size change re-pins to the end — no time window to outrun. The
  // old settle-window approach lost the pin when the history seam prepended or
  // images/mermaid grew seconds later (Alex 2026-07-04).
  const csInnerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const inner = csInnerRef.current;
    if (!inner) return;
    const ro = new ResizeObserver(() => {
      const el = streamRef.current;
      if (!el || !nearBottom.current || prependAnchor.current !== null) return;
      el.scrollTop = el.scrollHeight;
    });
    ro.observe(inner);
    return () => ro.disconnect();
  }, []);

  // READING-POSITION ANCHOR (Alex 2026-07-04): while scrolled UP (reading), content
  // changes (streaming growth, new turns, images) must not shift the viewport — iOS
  // Safari has no native scroll anchoring. Capture the topmost visible turn DURING
  // RENDER (DOM still shows the old layout), restore its position after commit.
  const readAnchorRef = useRef<{ key: string; delta: number } | null>(null);
  const prevDisplayTurnsRef = useRef(displayTurns);
  if (prevDisplayTurnsRef.current !== displayTurns) {
    prevDisplayTurnsRef.current = displayTurns;
    const el = streamRef.current;
    if (el && !nearBottom.current && prependAnchor.current === null) {
      const top = el.scrollTop;
      let anchor: { key: string; delta: number } | null = null;
      for (const child of Array.from((csInnerRef.current ?? el).children) as HTMLElement[]) {
        const key = child.dataset?.turnkey;
        if (!key) continue;
        if (child.offsetTop + child.offsetHeight > top) {
          anchor = { key, delta: top - child.offsetTop };
          break;
        }
      }
      readAnchorRef.current = anchor;
    } else {
      readAnchorRef.current = null;
    }
  }
  useLayoutEffect(() => {
    const a = readAnchorRef.current;
    const el = streamRef.current;
    if (!a || !el) return;
    readAnchorRef.current = null;
    const child = el.querySelector(`[data-turnkey="${CSS.escape(a.key)}"]`) as HTMLElement | null;
    if (child) el.scrollTop = child.offsetTop + a.delta;
  }, [displayTurns]);

  // Manual-scroll intent since opening THIS agent (Issue #211): wheel/touch = the user
  // deliberately moved the viewport. Content arriving late (history first, live turns
  // after) shifts scroll metrics WITHOUT user intent — nearBottom can't tell the two
  // apart. When the live view lands late and the user never scrolled → jump to bottom;
  // if they did scroll, respect their position.
  const userScrolledRef = useRef(false);
  const prevLiveCountRef = useRef(0);
  useEffect(() => {
    userScrolledRef.current = false;
    prevLiveCountRef.current = 0;
    // Fresh agent = fresh read: the previous agent's scroll position is meaningless,
    // we ALWAYS want to open at the bottom (Alex 2026-07-04).
    nearBottom.current = true;
  }, [sessionName]);
  useEffect(() => {
    const prev = prevLiveCountRef.current;
    prevLiveCountRef.current = liveTurns.length;
    if (prev === 0 && liveTurns.length > 0 && !userScrolledRef.current) {
      // Live view just arrived (late) + no manual scroll since open → pin to bottom.
      nearBottom.current = true;
      const el = streamRef.current;
      if (el) el.scrollTop = el.scrollHeight;
      settleToBottom(800);
    }
    // sessionName in the deps: on agent switch the SWR cache often paints instantly
    // with the SAME turn count as before → a length-only dep never fired and the view
    // kept the previous agent's scroll position instead of opening at the bottom
    // (Alex 2026-07-04).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveTurns.length, sessionName]);

  // "Jump to latest" button — shown once scrolled a good way up (Alex 2026-06-23).
  const [showScrollDown, setShowScrollDown] = useState(false);
  // True while a programmatic scroll-to-bottom animates: the smooth scroll
  // fires onScroll the whole way down (depth still >600 for a bit), which
  // would re-show the button mid-flight. This flag suppresses that until we
  // actually arrive at the bottom (Alex 2026-06-23: "du musst wissen, ob er
  // gerade im Runterfahr-Modus ist").
  const scrollingDownRef = useRef(false);
  const scrollToBottom = useCallback(() => {
    const el = streamRef.current;
    if (!el) return;
    scrollingDownRef.current = true;
    setShowScrollDown(false);
    nearBottom.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    // Safety net if the animation is interrupted and never reaches the floor.
    setTimeout(() => { scrollingDownRef.current = false; }, 1000);
  }, []);

  // Stick to the bottom across a settle window. A single `scrollTop =
  // scrollHeight` doesn't survive async-growing content (lazy mermaid, images,
  // markdown) or the first history batch prepending above — after F5 you'd
  // land mid-list (Alex 2026-06-23). This rAF loop re-pins each frame while
  // we're meant to be at the bottom, until the page stops growing or the user
  // scrolls up (nearBottom flips) / a scroll-up history load is adjusting.
  const settleRaf = useRef<number | null>(null);
  const settleToBottom = useCallback((durationMs: number) => {
    const end = performance.now() + durationMs;
    const step = () => {
      const el = streamRef.current;
      if (!el || !nearBottom.current || prependAnchor.current !== null) { settleRaf.current = null; return; }
      el.scrollTop = el.scrollHeight;
      settleRaf.current = performance.now() < end ? requestAnimationFrame(step) : null;
    };
    if (settleRaf.current) cancelAnimationFrame(settleRaf.current);
    settleRaf.current = requestAnimationFrame(step);
  }, []);
  useEffect(() => () => { if (settleRaf.current) cancelAnimationFrame(settleRaf.current); }, []);
  useEffect(() => () => { if (nameHoverTimer.current) clearTimeout(nameHoverTimer.current); }, []);

  const handleScroll = useCallback(() => {
    const el = streamRef.current;
    if (!el) return;
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    // Switch window: right after opening an agent, scroll events come from browser
    // clamping / SWR repaint / seam prepend — NOT from the user. Keep the bottom-pin
    // armed unless the user really touched (wheel/touch set userScrolledRef first).
    const inSwitchWindow = performance.now() - switchTsRef.current < 1500 && !userScrolledRef.current;
    nearBottom.current = fromBottom < 60 || inSwitchWindow;
    // Scrollbar-drag has no wheel/touch event — treat a real upward drift as
    // deliberate scrolling too, so auto-jumps never fire mid-read (Alex 2026-07-04).
    if (!scrollingDownRef.current && prependAnchor.current === null && fromBottom > 200 && !inSwitchWindow) {
      userScrolledRef.current = true;
    }
    if (scrollingDownRef.current) {
      // Auto-scrolling down — keep the button hidden; release once we land.
      if (fromBottom < 60) scrollingDownRef.current = false;
    } else {
      setShowScrollDown(fromBottom > 600);
    }
    // Near the top → pull the next older transcript page (#1204).
    const h = histRef.current;
    if (el.scrollTop < 80 && h.hasMore && !h.loadingOlder) {
      prependAnchor.current = el.scrollHeight;
      h.loadOlder();
    }
  }, []);

  // Keep the viewport stable when older turns prepend (no jump-to-top). Only
  // adjusts on an explicit scroll-up load (prependAnchor set); the initial
  // older-batch falls through to the stick-to-bottom effect.
  useLayoutEffect(() => {
    const el = streamRef.current;
    if (el && prependAnchor.current !== null) {
      const delta = el.scrollHeight - prependAnchor.current;
      // Skip the adjustment in the switch window: a stale/spurious anchor there would
      // shove the fresh view mid-history — the first batch must fall through to the
      // bottom-pin instead (the original design intent).
      const inSwitchWindow = performance.now() - switchTsRef.current < 1500;
      if (delta > 0 && !inSwitchWindow) el.scrollTop += delta;
      prependAnchor.current = null;
    }
  }, [hist.older]);

  // Clear sending indicator when new turn appears
  useEffect(() => {
    const newCount = data?.turn_count || 0;
    if (sending && newCount > turnCountRef.current) {
      setSending(false);
    }
    turnCountRef.current = newCount;
  }, [data?.turn_count, sending]);

  useEffect(() => {
    // Settle (not a one-shot) so async-loading content + the first prepended
    // history batch can't leave us mid-list after F5. Skip while a scroll-up
    // load is restoring the viewport (prependAnchor set).
    if (nearBottom.current && prependAnchor.current === null) {
      settleToBottom(800);
    }
    // `hist.older` so the FIRST older-batch (prepended above the live view at
    // load) keeps us pinned to the bottom instead of jumping up.
  }, [data, sending, hist.older, settleToBottom]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    const files = e.dataTransfer.files;
    if (files.length === 0) return;
    // Kein Storage auf dieser Instanz → Drop leise ignorieren statt eines
    // Uploads, der nur eine Fehlerkette produziert (77eb0c9).
    if (!storageAvailable) return;
    // Funnel into InputBar's attachments list so the user sees the same
    // thumbnail-with-X chips above the input as for paperclip uploads —
    // not the previous URL-pasted-into-textarea behaviour.
    await inputBarRef.current?.addFiles(files);
  }, []);

  // Heuristic upgrade of stale state when conversation polling has clearly
  // outpaced the agent-state poll. If the v2 response timestamp is newer
  // than ~15s after the agentState's snapshot AND the most recent turn has
  // an open tool / thinking section (no closing 'text' after it), treat
  // the agent as busy. Avoids the "CLOUD idle (green dot)" while the chat
  // is actively streaming Update/Bash sections (Alex's screenshot 109563).
  const backendState = agentState?.state || 'unknown';
  const lastTurnSeemsActive = (() => {
    const turns = data?.turns;
    if (!turns?.length) return false;
    const last = turns[turns.length - 1];
    const sections = last.sections || [];
    if (!sections.length) return false;
    // Walk from the end: if we hit a tool or thinking section before any
    // text section, the agent is mid-tool-loop.
    for (let i = sections.length - 1; i >= 0; i--) {
      const k = sections[i].kind;
      if (k === 'text' || k === 'json') return false;
      if (k === 'tool' || k === 'thinking') return true;
    }
    return false;
  })();
  // Activity freshness (Alex 2026-07-21): the login/error banner must not show
  // while the agent is DEMONSTRABLY alive. `needs_login`/`error` from the
  // agent-state poll can be STALE — the agent recovered and is streaming again,
  // but the poll still carries the old error → banner falsely appears ("manchmal
  // geht der agent und trotzdem schreibt er login erforderlich hin"). A working
  // agent grows the transcript continuously; a dead one (token revoked mid-tool-
  // call) freezes. So we stamp the last transcript change and trust an error-
  // state only once the transcript has been FROZEN for a bit. This is the inverse
  // guard to the blocking-state authority added 0c2111a (which fixed the OPPOSITE
  // bug: dead turn showing the active spinner).
  const BANNER_STATES = ['needs_login', 'out_of_credits', 'quota_exhausted', 'error'];
  const bannerCandidate = BANNER_STATES.includes(backendState);
  const activitySig = (() => {
    const t = data?.turns;
    if (!t?.length) return `c${data?.turn_count ?? 0}`;
    const last = t[t.length - 1];
    const secs = last.sections || [];
    const lastSec = secs[secs.length - 1];
    return `${data?.turn_count ?? 0}:${secs.length}:${lastSec?.content?.length ?? 0}`;
  })();
  const lastActivityRef = useRef<number>(Date.now());
  const prevSigRef = useRef(activitySig);
  if (prevSigRef.current !== activitySig) {
    prevSigRef.current = activitySig;
    lastActivityRef.current = Date.now();
  }
  const [, forceFreshTick] = useState(0);
  const ACTIVITY_WINDOW_MS = 15000;
  const recentlyActive = Date.now() - lastActivityRef.current < ACTIVITY_WINDOW_MS;
  // While an error-state is pending BUT the transcript is still fresh, tick so
  // the freshness window can expire and the banner appears once truly frozen.
  useEffect(() => {
    if (!bannerCandidate || !recentlyActive) return;
    const id = setInterval(() => forceFreshTick((n) => n + 1), 2000);
    return () => clearInterval(id);
  }, [bannerCandidate, recentlyActive]);

  // State derivation. `waiting` (permission-halt), `dead` and `rate_limited`
  // stay authoritative — deliberate backend signals with their own UI. The
  // banner states (needs_login/out_of_credits/error) are freshness-gated: while
  // the transcript is actively growing the agent is alive, so the stale block is
  // ignored (shown as thinking/idle); only a FROZEN transcript surfaces the block.
  const state =
    backendState === 'thinking' || backendState === 'tool'
      ? backendState
      : backendState === 'waiting' || backendState === 'dead' || backendState === 'rate_limited_transient'
        ? backendState
        : bannerCandidate
          ? (recentlyActive
              ? (lastTurnSeemsActive ? 'thinking' : 'idle')
              : backendState)
          : lastTurnSeemsActive
            ? 'thinking'
            : backendState;
  const agentBusy = state === 'thinking' || state === 'tool';
  // Hard blocks that must SUPPRESS every activity indicator (spinner/wave) and
  // instead surface a distinct banner — the agent is stopped, not working.
  const isBlocked = state === 'needs_login' || state === 'out_of_credits' || state === 'quota_exhausted' || state === 'error';
  // Alex 2026-08-07: „wenn jetzt ein Agent steht, okay Login Error — dann sehe
  // ich im User Interface, wie lange war der eingeloggt." Genau das, nicht mehr.
  // Abruf NUR wenn der Agent wirklich blockiert ist; scheitert er, bleibt die
  // Zeile weg. Eine erfundene Dauer wäre schlimmer als keine Angabe.
  const [tokenStats, setTokenStats] = useState<TokenStats | null>(null);
  useEffect(() => {
    if (!isBlocked) { setTokenStats(null); return; }
    let alive = true;
    void api.tokenStats(sessionName)
      .then((t) => {
        if (!alive) return;
        setTokenStats(t);
      })
      .catch(() => { if (alive) setTokenStats(null); });
    return () => { alive = false; };
  }, [isBlocked, sessionName]);

  // Aus den ABSOLUTEN Zeitstempeln gegen die laufende Uhr — nicht aus der
  // Momentaufnahme des Abrufs. Bleibt ein Agent eine Stunde blockiert, wandert
  // „seit X abgelaufen" mit, statt beim Wert von vorhin stehen zu bleiben
  // (Login hat den Drift zwischen zwei Messungen gerade selbst gezeigt).
  // Eigener Takt, der NUR läuft, solange der Agent blockiert ist — der große
  // `now` weiter unten hängt an anderen Bedingungen und existiert hier noch
  // nicht.
  const tokenNow = useNow(isBlocked, 30_000);
  // Reset-Zeitpunkt lesbar machen; ungültige/fehlende Angabe → kein Halbsatz.
  const quotaResetLabel = (() => {
    const iso = agentState?.resets_at;
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('de-AT', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  })();
  const loginAge = (() => {
    if (!tokenStats) return '';
    const { ageMin, expiresInMin } = liveAges(tokenStats, tokenNow);
    return [
      formatLoginAge(ageMin) && `war ${formatLoginAge(ageMin)} angemeldet`,
      formatExpiredSince(expiresInMin),
      formatVerdict(tokenStats.verdict),
    ].filter(Boolean).join(' · ');
  })();
  // VISUAL state for the header badge + dot (Issue #212): use what we KNOW before the
  // backend scraper catches up. Sending a prompt starts inference immediately → show
  // "arbeitet" at once instead of READY during the scrape-lag. ESC/Stop → show
  // INTERRUPTED at once instead of a lingering "arbeitet". Logic (agentBusy, voice,
  // queue-flush) stays on the REAL backend state — this is display-only.
  const displayState =
    stopped && (state === 'thinking' || state === 'tool') ? 'interrupted'
    : !stopped && (sending || busyHold) && (state === 'ready' || state === 'idle' || state === 'unknown') ? 'thinking'
    : state;
  // Inconsistent state (Alex 2026-07-02): the live /v2 view is empty but older history
  // is still shown → you'd read stale turns thinking they're the current state. Surface
  // it + let the user recover, instead of a silent history-only view that "makes no sense".
  const liveMissing = !!data && liveTurns.length === 0 && hist.older.length > 0 && state !== 'suspended';
  // Voice narrator: a turn/activity ending (busy → idle) or a needs-input/error
  // is a SEMANTIC BOUNDARY — narrate the accumulated feed now (hybrid trigger).
  const prevAgentBusyRef = useRef(agentBusy);
  useEffect(() => {
    if (voice.state === 'live' && ((prevAgentBusyRef.current && !agentBusy) || state === 'waiting' || state === 'error')) {
      voice.narrateBoundary();
    }
    prevAgentBusyRef.current = agentBusy;
  }, [agentBusy, state, voice]);
  // `/v2` is a timestamp-free pane scrape. Refresh transcript timing once the
  // whole optimistic/live busy window closes, including very fast turns that
  // finish before the backend scraper ever exposes `thinking`/`tool`.
  const timingBusy = agentBusy || sending || busyHold;
  const prevTimingBusyRef = useRef(timingBusy);
  useEffect(() => {
    if (prevTimingBusyRef.current && !timingBusy) hist.refreshNewest();
    prevTimingBusyRef.current = timingBusy;
  }, [timingBusy, hist.refreshNewest]);
  // Release the bridge once the agent turn ends (busy went true → false), and a
  // hard safety timeout so a no-op send never holds the indicator forever.
  const prevBusyRef = useRef(false);
  useEffect(() => {
    if (prevBusyRef.current && !agentBusy) {
      setBusyHold(false);
      // The agent genuinely stopped (busy→idle) → clear the optimistic-stop
      // flag. Crucial so a LATER turn (e.g. an incoming IACP message) shows
      // the stop button again, while a still-busy agent keeps it hidden.
      setStopped(false);
    }
    prevBusyRef.current = agentBusy;
  }, [agentBusy]);
  // Physical ESC key interrupts a BUSY agent from the laptop — same as clicking Stop
  // (Alex 2026-06-26: "esc weitergeleitet an den agenten"). Only while the agent is
  // working (the Stop button's condition) and NOT while typing in a field/modal input,
  // so composing a message isn't disrupted. The agent receives the double-ESC interrupt.
  useEffect(() => {
    const busy = (sending || agentBusy || busyHold) && !stopped;
    if (!busy) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const el = document.activeElement as HTMLElement | null;
      // Allow ESC to forward from the message TEXTAREA too (Alex 2026-06-26 — it has
      // no local ESC behaviour, nothing is lost). Still skip single-line INPUTs +
      // contentEditable (search boxes / modal fields, where ESC means close/clear).
      if (el && (el.tagName === 'INPUT' || el.isContentEditable)) return;
      e.preventDefault();
      stopAgent();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sending, agentBusy, busyHold, stopped, stopAgent]);
  useEffect(() => {
    if (!busyHold) return;
    const t = setTimeout(() => setBusyHold(false), 12000);
    return () => clearTimeout(t);
  }, [busyHold]);

  // Rate-limit countdown — Cloud-API exposes stamped_at + retry_after on
  // the agent-state row (commit 31a2735). The watchdog stamps when it
  // first sees the marker, waits retry_after seconds, then sends
  // "plz continue". Tick only while this bot is actually rate-limited
  // so we don't burn CPU at 1Hz in steady state.
  const rlStamped = agentState?.rate_limit_stamped_at;
  const rlRetryAfter = agentState?.rate_limit_retry_after;
  const rlActive = state === 'rate_limited_transient' && (rlStamped ?? 0) > 0;
  const liveStatus = agentState?.live_status;
  const now = useNow(rlActive || !!liveStatus);
  const rlEta = rlActive ? computeNudgeEta(rlStamped, rlRetryAfter, now) : null;
  const rlPct = rlActive && rlEta !== null && rlRetryAfter
    ? Math.min(100, Math.max(0, ((rlRetryAfter - rlEta) / rlRetryAfter) * 100))
    : 0;

  return (
    <div
      ref={glassContainerRef}
      className={`agent-detail-container${dragging ? ' drag-over' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <header
        ref={glassHeaderRef}
        className={`agent-header${headerExpanded ? ' expanded' : ''}`}
        onClick={(e) => {
          // Click on the header BACKGROUND expands it (the group chats). Clicks
          // on the name (context menu), buttons, dot, inputs are excluded.
          if ((e.target as HTMLElement).closest('button, input, a, .agent-name, .status-dot, .meta-tag')) return;
          if (onOpenCollab || onAskGroup) setHeaderExpanded((v) => !v);
        }}
      >
        {onBack && (
          <button className="back-button" onClick={onBack} title="Back">
            <ArrowLeft size={16} />
          </button>
        )}
        {/* Header dot — runtime-state + lifecycle semantics (Alex's
            spec via Cloud IACP 628645e1, supersedes the activity_class
            mapping). Mirrors SessionList exactly so the header and
            sidebar always agree:
              error    → red, working → orange-pulse, offline → grey,
              available → green. */}
        <div className={`status-dot ${(() => {
          // displayState (not raw state): reflects the optimistic send/stop knowledge
          // so the dot flips to working the moment a prompt is sent (#212).
          const isErr = displayState === 'error' || displayState === 'rate_limited_transient' || displayState === 'needs_login';
          // Lifecycle-dead = process CONFIRMED not running (session_manager).
          // A confirmed-dead bot stays grey even if a stale thinking-state
          // lingers. But `actually_running === false` alone is a weaker
          // detection signal that lags — it must NOT override a LIVE
          // working state (Alex 2026-06-16: grey dot while agent visibly
          // thinking + Stop button showing = actually_running lag).
          const isErrState = isErr;
          const lifecycleDead = session?.status === 'suspended' || session?.status === 'dead'
            || session?.status === 'orphan' || session?.status === 'recovering';
          const isWorking = displayState === 'thinking' || displayState === 'tool';
          if (isErrState) return statusDotClass(displayState);
          if (lifecycleDead) return 'offline';
          if (isWorking) return 'working';          // beats actually_running lag
          // Permission-blocked: distinct "waiting for you" dot, NOT green.
          if (displayState === 'waiting') return 'waiting';
          if (session?.actually_running === false) return 'offline';
          return 'available';
        })()}`} />
        <div
          className="agent-name agent-name-clickable"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setHeaderMenu({ x: rect.left, y: rect.bottom + 4 });
          }}
          onMouseEnter={(e) => {
            if (!HOVER_CAPABLE) return;
            const rect = e.currentTarget.getBoundingClientRect();
            if (nameHoverTimer.current) clearTimeout(nameHoverTimer.current);
            nameHoverTimer.current = setTimeout(() => setNameHoverRect(rect), 350);
          }}
          onMouseLeave={() => {
            if (nameHoverTimer.current) clearTimeout(nameHoverTimer.current);
            setNameHoverRect(null);
          }}
          title="Aktionen: Klonen, Restart, Reset, Edit, Kill…"
        >
          <span className="agent-name-text" title={session?.display_name ? sessionName : undefined}>{session?.display_name || sessionName}</span>
          {/* Tiny running-model tag right beside the name (Alex 2026-07-14). Show the
              CANONICAL model string verbatim (only trim a `claude-` prefix for brevity) —
              NOT prettyModel, which title-cased codex ids into wrong labels like
              "GPT 5.6 Sol" (Alex: it's `gpt-5.6-sol`, not "gpt 5.6"). */}
          {(() => {
            const raw = String(data?.metrics?.model || session?.model || '');
            if (!raw) return null;
            return (
              <span className="agent-name-model" title={`Modell: ${raw}`}>{raw.replace(/^claude-/, '')}</span>
            );
          })()}
          {/* Visible affordance so it's obvious the name opens the action menu
              (Klonen / Restart / Reset / Kill …) — Alex 2026-06-23. */}
          <ChevronDown size={13} className="agent-name-caret" />
        </div>
        {/* The lowercase state word + the Turns/MCP counters were removed (Alex #164):
            the state already shows in the badge below (no duplicate), and the counters
            ate scarce header width. Model stays (it's the one genuinely useful tag). */}
        {headerStatus && <div className="header-action-status">{headerStatus}</div>}
        <div className="agent-meta">
          {/* Model moved up next to the name (Alex 2026-07-14) — no longer duplicated here. */}
          {queueCount > 0 && (
            <div className="meta-tag meta-queued" title={`${queueCount} Issue(s) warten auf diesen Agenten (Back-Pressure-Queue) — werden nacheinander dispatcht`}>
              <span className="label">Queue</span> {queueCount} wartend
            </div>
          )}
          <span
            className={`badge ${stateBadgeClass(displayState)}${rlActive ? ' badge-rate-limited' : ''}`}
            title={rlActive && rlEta !== null
              ? rlEta > 0
                ? `Anthropic transient rate-limit — watchdog nudges with "plz continue" in ${formatMmSs(rlEta)}`
                : 'Watchdog sending "plz continue" nudge…'
              : undefined}
          >
            {stateLabel(displayState)}
            {rlActive && rlEta !== null && (
              <span className="badge-countdown">
                · {rlEta > 0 ? `nudge in ${formatMmSs(rlEta)}` : 'nudging…'}
              </span>
            )}
            {rlActive && (
              <span
                className="badge-progress"
                style={{ width: `${rlPct}%` }}
                aria-hidden="true"
              />
            )}
          </span>
        </div>
        {/* Explicit header actions (Alex 2026-06-22): a discoverable button for
            the group-chats dropdown + a "+" to start a new group chat — so it's
            obvious there's something to tap, not just a background click. */}
        <div className="agent-header-actions">
          {/* Realtime voice narrator (Step 1, read-only) — listens to this
              agent's live output + narrates in an AgentOS voice. #1215. Only
              shown when the account is realtime-enabled (#1220 access control);
              the real billing gate is server-side at the AiApi mint. */}
          {voice.canUseVoice && (
            <button
              className={`ahx-btn voice-btn${voiceLive ? ' on voice-live' : ''}`}
              onClick={() => (voiceLive ? voice.stop() : setVoiceStartOpen(true))}
              title={voiceLive ? 'Voice stoppen' : 'Realtime-Voice starten (Modus + Detailgrad wählen)'}
              disabled={voice.state === 'connecting'}
            >
              <AudioLines size={15} className={voiceLive ? 'voice-pulse' : undefined} />
            </button>
          )}
          {/* History-tags (Cloud 2026-06-23): bookmark + rewind. A filled
              bookmark + "on" highlight when the agent is currently rewound. */}
          <button
            className={`ahx-btn${tagPanelOpen ? ' on' : ''}${pinnedTag ? ' pinned' : ''}`}
            onClick={() => { setTagPanelOpen((v) => !v); setHeaderExpanded(false); }}
            title={pinnedTag ? `Zurückgespult auf „${pinnedTag}"` : 'Lesezeichen / Zurückspulen'}
          >
            <Bookmark size={15} fill={pinnedTag ? 'currentColor' : 'none'} />
            {tagsApi.tags.length > 0 && <span className="ahx-btn-count">{tagsApi.tags.length}</span>}
          </button>
          {(onOpenCollab || onAskGroup) && (
            <button
              className={`ahx-btn${headerExpanded ? ' on' : ''}`}
              onClick={() => { setHeaderExpanded((v) => !v); setTagPanelOpen(false); }}
              title="Gruppenchats"
            >
              <MessagesSquare size={15} />
              <ChevronDown size={11} className={`ahx-btn-caret${headerExpanded ? ' open' : ''}`} />
            </button>
          )}
          {onAskGroup && (
            <button
              className="ahx-btn"
              onClick={() => { setHeaderExpanded(false); onAskGroup(sessionName); }}
              title="Neuer Gruppenchat"
            >
              <Plus size={16} />
            </button>
          )}
          {issueAvailable && (
          <button
            className={`ahx-btn${issuePanelOpen ? ' on' : ''}${openIssueCount > 0 ? ' has-issues' : ''}`}
            onClick={() => { setHeaderExpanded(false); setTagPanelOpen(false); setIssuePanelOpen((v) => !v); }}
            title={openIssueCount > 0
              ? `${openIssueCount} offene(s) Issue(s) für diesen Agenten — pushen / abschließen / verwerfen`
              : 'Zugeordnete Issues (offen) — pushen / abschließen / verwerfen'}
          >
            <ListChecks size={15} />
            {openIssueCount > 0 && <span className="ahx-btn-count">{openIssueCount}</span>}
          </button>
          )}
        </div>
      </header>

      {/* Per-agent issue panel — open issues assigned to this agent (Alex 2026-06-29). */}
      {issuePanelOpen && (
        <AgentIssues sessionName={sessionName} onClose={() => setIssuePanelOpen(false)} />
      )}

      {/* History-tags panel — bookmark the current point, rewind to any tag. */}
      {tagPanelOpen && (
        <TagPanel
          tags={tagsApi.tags}
          loading={tagsApi.loading}
          resuming={tagsApi.resuming}
          pinnedTag={pinnedTag}
          onBookmark={bookmarkNow}
          onRemove={tagsApi.removeTag}
          onResume={doResume}
        />
      )}

      {/* Expanded header — the group chats this agent is in (Alex 2026-06-22:
          click the header background OR the explicit dropdown button to expand). */}
      {headerExpanded && (
        <div className="agent-header-expand">
          <div className="ahx-top">
            <span className="ahx-label">Gruppenchats</span>
            {onAskGroup && (
              <button className="ahx-new" onClick={() => { setHeaderExpanded(false); onAskGroup(sessionName); }}>
                <Plus size={14} /> Neu
              </button>
            )}
          </div>
          {groupChats.length > 4 && (
            <input
              className="ahx-filter"
              placeholder="Filtern…"
              value={gcFilter}
              onChange={(e) => setGcFilter(e.target.value)}
            />
          )}
          <div className="ahx-list">
            {groupChats
              .filter((p) => !gcFilter || (p.title || '').toLowerCase().includes(gcFilter.toLowerCase()))
              .slice(0, 10)
              .map((p) => (
                <button
                  key={p.id}
                  className="ahx-item"
                  onClick={() => { setHeaderExpanded(false); onOpenCollab?.(p.id); }}
                >
                  <MessagesSquare size={14} />
                  <span className="ahx-item-title">{p.title || `Collab #${p.id}`}</span>
                </button>
              ))}
            {groupChats.length === 0 && (
              <div className="ahx-empty">
                {groupChatsError
                  ? '⚠ Gruppenchats konnten nicht geladen werden (Server-Fehler #231)'
                  : 'In keinem Gruppenchat'}
              </div>
            )}
          </div>
        </div>
      )}

      {headerMenu && (
        <>
          <div className="ctx-backdrop" onClick={() => setHeaderMenu(null)} />
          <div
            className="ctx-menu"
            style={{ left: headerMenu.x, top: headerMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="ctx-item"
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setHeaderMenu(null);
                setShowInfo(true);
              }}
            >
              <Info size={14} />
              Info
            </button>
            <button
              className="ctx-item"
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setHeaderMenu(null);
                setShowPrompt(true);
              }}
            >
              <FileCode size={14} />
              Prompt
            </button>
            <div className="ctx-divider" />
            {([
              { label: 'Resume',  action: 'resume',  Icon: Play },
              { label: 'Suspend', action: 'suspend', Icon: Pause },
              { label: 'Restart', action: 'restart', Icon: RefreshCw },
              { label: 'Auto-Login',    action: 'login',        Icon: KeyRound },
              { label: 'Login manuell', action: 'login-manual', Icon: KeyRound },
            ] as const).map((item) => (
              <button
                key={item.action}
                className="ctx-item"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  runHeaderAction(item.action);
                }}
              >
                <item.Icon size={14} />
                {item.label}
              </button>
            ))}
            <div className="ctx-divider" />
            {onEditAgent && (
              <button
                className="ctx-item"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setHeaderMenu(null);
                  if (session) onEditAgent(session);
                }}
              >
                <Pencil size={14} />
                Edit Agent
              </button>
            )}
            {onSwitchAgent && session && (
              <button
                className="ctx-item"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setHeaderMenu(null);
                  onSwitchAgent(session);
                }}
              >
                <Bot size={14} />
                CLI / LLM wechseln
              </button>
            )}
            <button
              className="ctx-item"
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setHeaderMenu(null);
                setShowClone(true);
              }}
            >
              <Copy size={14} />
              Klonen
            </button>
            {instanceMode === 'federation' && (
              <button
                className="ctx-item"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setHeaderMenu(null);
                  setShowMove(true);
                }}
              >
                <Server size={14} />
                Verschieben
              </button>
            )}
            <button
              className="ctx-item"
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setHeaderMenu(null);
                setShowMcp(true);
              }}
            >
              <Server size={14} />
              MCP Server
            </button>
            <div className="ctx-divider" />
            <button
              className="ctx-item"
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setHeaderMenu(null);
                setShowReset(true);
              }}
              title="Soft Reset — agent updates its /memory, conversation continues"
            >
              <RotateCcw size={14} />
              Soft Reset
            </button>
            <button
              className="ctx-item"
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                runHeaderAction('hard-reset');
              }}
              title="Hard Reset — wipe conversation history, fresh start (no memory update)"
            >
              <Eraser size={14} />
              Hard Reset
            </button>
            <button
              className="ctx-item ctx-destructive"
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                runHeaderAction('kill');
              }}
            >
              <Power size={14} />
              Kill
            </button>
            <div className="ctx-footer">{sessionName}</div>
          </div>
        </>
      )}

      {showInfo && session && (
        <AgentInfoModal
          session={session}
          state={agentState}
          mcpCount={mcpCount}
          onClose={() => setShowInfo(false)}
        />
      )}

      {showPrompt && (
        <PromptInspectorModal
          sessionName={sessionName}
          poolName={session?.pool_name || undefined}
          onClose={() => setShowPrompt(false)}
        />
      )}

      {showMcp && (
        <>
          <div className="ctx-backdrop" onClick={() => setShowMcp(false)} />
          <div className="mcp-modal-wrap">
            <McpPanel
              sessionName={sessionName}
              onClose={() => setShowMcp(false)}
              onStatus={(msg) => {
                setHeaderStatus(msg);
                setTimeout(() => setHeaderStatus(''), 2000);
              }}
            />
          </div>
        </>
      )}

      {showReset && (
        <ResetModal
          sessionName={sessionName}
          onClose={() => setShowReset(false)}
        />
      )}

      {showClone && (
        <CloneDialog
          sessionName={sessionName}
          source={session ?? null}
          tags={tagsApi.tags}
          onClose={() => setShowClone(false)}
          onCloned={(n) => onSelectSession?.(n)}
        />
      )}

      {showMove && (
        <MoveDialog
          sessionName={sessionName}
          source={session ?? null}
          onClose={() => setShowMove(false)}
        />
      )}

      {showLogin && (
        <AgentLoginModal
          sessionName={sessionName}
          mode={showLogin}
          agent={session?.agent}
          onClose={() => setShowLogin(false)}
        />
      )}

      {nameHoverRect && session && (
        <AgentHoverTooltip
          session={session}
          // Header already has the loaded V2 data — no extra fetch needed.
          metrics={{
            turnCount: data?.turn_count,
            tokens: data?.metrics?.input_tokens_approx,
            bytes: data?.metrics?.transcript_bytes,
            model: data?.metrics?.model,
            effort: data?.metrics?.effort,
          }}
          rect={nameHoverRect}
        />
      )}

      <div className="content-stream-wrap">
      {/* Pre-start config dialog — pick mode/detail, then Start (Alex 2026-06-26). */}
      {voiceStartOpen && (
        <VoiceStartDialog
          onStart={() => voice.start(sessionName)}
          onClose={() => setVoiceStartOpen(false)}
        />
      )}
      {/* Realtime voice narrator — slim live status bar (Step 1.5, read-only).
          The <audio> sink lives app-level (GlobalVoiceLayer) so it survives
          navigation; this bar only renders while this agent is in view. #1215. */}
      {voiceLive && (
        <div className={`voice-bar voice-bar-${voice.state}`}>
          {/* ZWEI feste Zeilen statt freiem Umbruch (Alex 2026-08-11): Vorher
              lagen alle Bedienelemente als Geschwister in einem umbrechenden
              Flex-Container — je nach Breite ergaben sich drei zerfranste Reihen
              mit dem Stopp-Knopf allein unten rechts. Jetzt trägt Zeile 1 den
              Zustand und die einzige Handlung, die im Gespräch zählt (halten
              zum Sprechen, stoppen); Zeile 2 alles Einstellende. Was man tut,
              steht oben; was man einstellt, unten. */}
          <div className="voice-bar-row primary">
          <AudioLines size={13} className="voice-pulse" />
          <span className="voice-bar-label">
            {voice.state === 'connecting'
              ? 'Voice verbindet…'
              : `${voice.runningModeLabel} · Push-to-Talk`}
          </span>
          {lastVoiceLine && (
            <span className="voice-bar-last">„{lastVoiceLine.text.slice(0, 80)}…"</span>
          )}
          {/* Alternative PTT surface for the conversation view. The avatar video
              uses the same hook; both close fail-safe on cancel/lost capture. */}
          <button
            type="button"
            className={`voice-bar-mic ptt${voice.inputMuted ? '' : ' talking'}`}
            disabled={voice.state !== 'live'}
            onPointerDown={(event) => {
              if (!event.isPrimary) return;
              event.preventDefault();
              try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* unsupported */ }
              voice.pushToTalk(true);
            }}
            onPointerUp={(event) => {
              if (!event.isPrimary) return;
              voice.pushToTalk(false);
              try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* already released */ }
            }}
            onPointerCancel={() => voice.pushToTalk(false)}
            onLostPointerCapture={() => voice.pushToTalk(false)}
            onContextMenu={(event) => event.preventDefault()}
            title="Gedrückt halten zum Sprechen — sonst erreicht kein Mikrofon-Audio OpenAI"
          >
            {voice.inputMuted ? <MicOff size={12} /> : <Mic size={12} />} Halten z. Sprechen
          </button>
          {/* Live mic-level meter (Alex 2026-06-26): dances when the mic captures audio,
              flat when nothing reaches it — the direct answer to "does my voice arrive?". */}
          <MicLevelMeter stream={voice.micStream} active={voice.state === 'live' && !voice.inputMuted} />
          <button type="button" className="voice-bar-stop" onClick={voice.stop} title="Stoppen">
            <Square size={11} />
          </button>
          </div>
          <div className="voice-bar-row secondary">
          {/* Focus-lock toggle (Alex 2026-06-24): wenn aktiv, bleibt die Stimme
              auf DEINEM fokussierten Agenten — kein Ambient-Wandern, und Talkback
              geht an genau diesen Agenten. Live umschaltbar während der Session. */}
          <button
            type="button"
            className={`voice-bar-mic focus-lock${voice.ambientMode === 'focus' ? ' on' : ''}`}
            onClick={() => voice.setAmbientMode(voice.ambientMode === 'ambient' ? 'focus' : 'ambient')}
            title={voice.ambientMode === 'focus'
              ? 'Fokus gesperrt — bleibt auf diesem Agenten, kein Ambient (antippen für Ambient)'
              : 'Ambient — wandert bei Idle zu aktiven Agenten (antippen zum Fokus-Sperren)'}
          >
            {voice.ambientMode === 'focus' ? <Pin size={12} /> : <Compass size={12} />}
          </button>
          <AudioInputPicker
            devices={voice.audioInputs}
            selectedId={voice.selectedAudioInputId}
            activeLabel={voice.activeAudioInputLabel}
            switching={voice.audioInputSwitching}
            error={voice.audioInputError}
            disabled={voice.state !== 'live'}
            onChange={(deviceId) => { void voice.selectAudioInput(deviceId); }}
          />
          {/* Verify-Log: was gefüttert wurde ↔ was der Narrator sagte (Alex). */}
          <button
            type="button"
            className={`voice-bar-log${voiceLogOpen ? ' on' : ''}`}
            onClick={() => setVoiceLogOpen((v) => !v)}
            title="Verify-Log: Feed ↔ Narration"
          >
            <FileCode size={12} /> Log{voice.transcript.length > 0 ? ` (${voice.transcript.length})` : ''}
          </button>
          </div>
        </div>
      )}
      {/* Talkback confirm-chip (Step 2): the narrator proposed sending a command
          to an agent; Cloud's gate wants confirmation (risky / cross-focus). */}
      {/* Zentriert statt in die Sprachleiste gequetscht (Alex, 14.08.: „die
          Darstellung entspricht nicht der Qualität von dem, was der
          Realtime-Agent kann — die Fragestellung zentraler positionieren").
          Es IST eine Entscheidung, die alles andere pausiert — dann darf sie
          auch so aussehen. Kein Fokus-Raub: Sprech-Taste bleibt am Fenster,
          nur der Blick wird geführt. */}
      {voice.pendingProposal && (
        <div className="voice-proposal-overlay" role="dialog" aria-label="Sprachvorschlag bestätigen">
          <div className={`voice-proposal${voice.pendingProposal.danger !== 'none' ? ' risky' : ''}`}>
            <div className="voice-proposal-kicker">
              <Megaphone size={14} />
              <span>Talkback-Vorschlag</span>
              {voice.pendingProposal.crossFocus && <span className="voice-proposal-tag cross">anderer Agent</span>}
              {voice.pendingProposal.danger !== 'none' && <span className="voice-proposal-tag danger">{voice.pendingProposal.danger}</span>}
            </div>
            <div className="voice-proposal-question">An <b>{voice.pendingProposal.target}</b> senden?</div>
            <div className="voice-proposal-text">„{voice.pendingProposal.text}"</div>
            <div className="voice-proposal-actions">
              <button type="button" className="voice-proposal-ok" onClick={() => voice.confirmPendingProposal()}>Senden</button>
              <button type="button" className="voice-proposal-no" onClick={voice.rejectPendingProposal}>Verwerfen</button>
            </div>
          </div>
        </div>
      )}
      {/* Verify panel — the narration-fidelity trail (Alex 2026-06-23): each
          [→ gefüttert] is the agent text we sent the narrator, each [🔊 Narrator]
          is what it spoke. Compare against the visible agent chat. */}
      {voiceLive && voiceLogOpen && (
        <div className="voice-log">
          {voice.transcript.length === 0 && <div className="voice-log-empty">Noch nichts — warte auf Agent-Output…</div>}
          {voice.transcript.map((l, i) => (
            <div key={i} className={`voice-log-line voice-log-${l.role}`}>
              <span className="voice-log-tag">{l.role === 'fed' ? '→ gefüttert' : l.role === 'voice' ? '🔊 Narrator' : 'Du'}</span>
              {/* Verify-Panel attribution (Codex gate): kind · agent · epoch/seg
                  per fed line, so mis-attribution after a focus shift is visible. */}
              {l.role === 'fed' && l.kind && (
                <span className="voice-log-attr">{l.kind} · {l.agent} · e{l.epoch}/s{l.seg}</span>
              )}
              <span className="voice-log-text">{l.text}</span>
            </div>
          ))}
          {voice.usage.unsent > 0 && (
            <div className="voice-log-meta">⚠ {voice.usage.unsent} Usage-Events nicht bestätigt (Kosten = Untergrenze)</div>
          )}
          {voice.usage.acked > 0 && voice.allUsageAcked && (
            <div className="voice-log-meta">✓ {voice.usage.acked} Usage-Events bestätigt</div>
          )}
        </div>
      )}
      {voice.state === 'error' && voice.error && (
        <div className="voice-bar voice-bar-error">
          <span className="voice-bar-label">Voice: {voice.error}</span>
          <button type="button" className="voice-bar-stop" onClick={voice.stop}><Square size={11} /></button>
        </div>
      )}
      {/* Session-Metrik-Bericht nach Stop (Codex 2026-06-24): die exakten
          Ist-Werte zum Ablesen/Melden. */}
      {/* Voice-Session-Bericht erscheint beim Stop NICHT mehr automatisch (Alex
          2026-06-24: "nervig, ich muss immer X klicken") — nur noch, falls es ein
          echtes Usage-Problem gab (unsent>0). Der Log liegt eh auto-upgeloadet
          am Server. */}
      {voice.state === 'stopped' && voice.meta.startedAt > 0 && voice.usage.unsent > 0 && !voiceReportHidden && (
        <div className="voice-report">
          <div className="voice-report-head">
            <span>Voice-Session-Bericht</span>
            <button type="button" onClick={downloadVoiceLog} title="Vollen Log als JSON exportieren (Feed ↔ Narration, mit Timestamps)" style={{ marginLeft: 'auto' }}>
              <Download size={12} /> Log
            </button>
            <button type="button" onClick={() => setVoiceReportHidden(true)} title="Schließen"><X size={12} /></button>
          </div>
          <div className="voice-report-grid">
            <span>Session-ID</span><b>{voice.meta.id}</b>
            <span>Dauer</span><b>{Math.max(0, Math.round(((voice.meta.endedAt || Date.now()) - voice.meta.startedAt) / 1000))}s</b>
            <span>acked</span><b>{voice.usage.acked}</b>
            <span>pending</span><b>{voice.usage.pending}</b>
            <span>unsent</span><b>{voice.usage.unsent}</b>
            <span>unsentTokens</span><b>{voice.usage.unsentTokens}</b>
            <span>allUsageAcked</span><b>{voice.allUsageAcked ? 'true' : 'false'}</b>
            <span>Stop</span><b>sauber (kein Fehler, Mic beendet)</b>
          </div>
        </div>
      )}
      <div
        className={`content-stream${liveMissing ? ' content-stream-stale' : ''}`}
        ref={streamRef}
        onScroll={handleScroll}
        // Deliberate scroll intent (wheel/finger) — NOT programmatic scrolls, which also
        // fire onScroll. Gates the late-live-view auto-jump-to-bottom (Issue #211).
        onWheel={() => { userScrolledRef.current = true; }}
        onTouchMove={() => { userScrolledRef.current = true; }}
      >
        {/* Inner wrapper = ResizeObserver target for the persistent bottom-pin:
            ANY content growth (seam prepend, images, mermaid, streaming) re-pins
            while the user hasn't scrolled up (Alex 2026-07-04 "manchmal bin ich
            echt irgendwo"). */}
        <div className="cs-inner" ref={csInnerRef}>
        {/* Resume-at-tag pin banner — the agent is rewound to a bookmark; its
            history is branched (recoverable). "Zur vollen History" un-pins. */}
        {pinnedTag && (
          <div className="resume-pin-banner">
            <span className="resume-pin-banner-main">
              <Rewind size={14} />
              Zurückgespult auf <span className="resume-pin-banner-tag">„{pinnedTag}"</span>
            </span>
            <button
              className="resume-pin-clear"
              onClick={doClearResume}
              disabled={tagsApi.resuming === '__clear__'}
            >
              {tagsApi.resuming === '__clear__' ? 'Stelle her…' : 'Zur vollen History'}
            </button>
          </div>
        )}
        {/* Orphan cold-start states (Alex 2026-06-23). */}
        {wakeState === 'waking' && (
          <div className="wake-card">
            <span className="cmd-spinner" />
            <div className="wake-title">Starte {sessionName}…</div>
            <div className="wake-detail">Agent wird hochgefahren (Resume / Cold-Start) — das dauert ~10–15 s.</div>
          </div>
        )}
        {wakeState === 'blocked' && (
          <div className="wake-card">
            <div className="suspended-icon"><Pause size={32} /></div>
            <div className="wake-title">Stillgelegt</div>
            <div className="wake-detail">{wakeMsg}</div>
          </div>
        )}
        {wakeState === 'error' && (
          <div className="wake-card">
            <div className="wake-title">Cold-Start fehlgeschlagen</div>
            <div className="wake-detail">{wakeMsg}</div>
            <button className="suspended-action" onClick={retryWake}>Erneut versuchen</button>
          </div>
        )}
        {/* Live Now/Next card (Cloud IACP 308c9a3b) — shows WHAT the agent
            is doing during an active turn, with a live elapsed counter.
            Present only while working; on turn-end it vanishes and the
            post-turn recap card takes over (now→recap handoff). */}
        {liveStatus && (
          <div className="nownext-card">
            <div className="nownext-now">
              <span className="nownext-spinner" />
              {/* activity often null (whimsy spinner, no Todo) — generic
                  fallback per Cloud IACP cdd6506d. */}
              <span className="nownext-act">{liveStatus.activity || 'arbeitet…'}</span>
              {/* started_at can be absent on a transient capture (Next/Todo
                  line but no spinner-time) — rendering now-undefined gave
                  "NaNs" (Cloud IACP d6a71da4). Contract: present → timer. */}
              {Number.isFinite(liveStatus.started_at) && (
                <span className="nownext-elapsed">{Math.max(0, Math.round(now - liveStatus.started_at))}s</span>
              )}
            </div>
            {liveStatus.next && (
              <div className="nownext-next"><span className="nownext-next-label">Next</span> {liveStatus.next}</div>
            )}
          </div>
        )}
        {/* Suspended-state placeholder: the v2 endpoint returns turns:[]
            for suspended bots — without this card the body is blank and
            the user sees a "white page" with only the header. Click
            triggers the same resume action as the header menu so the
            agent wakes up and the conversation re-renders on the next
            poll tick. */}
        {state === 'suspended' && wakeState === 'idle' && (
          <div className="suspended-card">
            <div className="suspended-icon"><Pause size={32} /></div>
            <div className="suspended-title">Agent suspended</div>
            <div className="suspended-detail">
              Diese Session ist pausiert. Beim Resume wird die Conversation aus dem letzten Stand fortgesetzt.
            </div>
            <button
              type="button"
              className="suspended-action"
              onClick={() => runHeaderAction('resume')}
              disabled={headerStatus.startsWith('resume')}
            >
              <Play size={14} />
              {headerStatus.startsWith('resume') ? headerStatus : 'Resume'}
            </button>
          </div>
        )}

        {/* Blocking-state banner (Cloud IACP fb6dd9b2): needs_login / out_of_credits /
            error killed the turn. Distinct, action-able — replaces the misleading
            "aktiv"-spinner (now suppressed via isBlocked). needs_login couples to the
            auto-heal reseed: while Cloud's reseed runs (detail carries 'reseed'), show
            "wird automatisch repariert"; otherwise offer the manual Login button. */}
        {isBlocked && (
          <div className={`agent-block-banner block-${state}`}>
            <span className="agent-block-icon">
              {state === 'needs_login' ? <KeyRound size={15} /> : <Power size={15} />}
            </span>
            <span className="agent-block-text">
              {state === 'needs_login' && (
                (agentState?.detail || '').toLowerCase().includes('reseed')
                  ? <>Login abgelaufen — <strong>wird automatisch repariert…</strong></>
                  : <>Login nötig — der Zugang ist abgelaufen{(agentState?.detail || '').toLowerCase().includes('revoked') ? ' (Token widerrufen)' : ''}. Wird automatisch repariert; falls nicht, manuell einloggen.</>
              )}
              {state === 'out_of_credits' && <>Credits leer — das Konto hat kein Guthaben mehr. Der Agent kann erst nach Aufladen weiterarbeiten.</>}
              {/* Kontingent (Rate-Limit) statt Guthaben: es füllt sich von
                  selbst, der Kunde muss NICHTS tun — deshalb Zeitpunkt statt
                  Handlungsaufforderung (Vertrag mit Cloud, 18.08.). Cloud
                  meldet den Zustand nur, solange er gilt; er verschwindet
                  ohne Zutun. */}
              {state === 'quota_exhausted' && (
                <>Kontingent aufgebraucht — dieser Agent pausiert{agentState?.reason ? <> ({agentState.reason})</> : null}
                  {quotaResetLabel ? <>, wieder verfügbar <strong>{quotaResetLabel}</strong></> : null}. Keine Aktion nötig, die Freigabe passiert automatisch.</>
              )}
              {state === 'error' && <>Fehler — der Agent ist in einem Fehlerzustand{agentState?.detail ? <> ({agentState.detail})</> : null}. Neu starten oder Logs prüfen.</>}
            </span>
            {loginAge && (
              <span className="agent-block-age" title="Lebensdauer, Ablauf und Befund des Zugangs — von Login (token-stats)">
                {loginAge}
              </span>
            )}
            {state === 'needs_login' && (
              <button type="button" className="agent-block-btn" onClick={() => setShowLogin('both')}>
                <KeyRound size={13} /> Einloggen
              </button>
            )}
          </div>
        )}

        {/* Live view missing but older history present → surface it prominently so the
            stale history isn't mistaken for the current state (Alex 2026-07-02). */}
        {liveMissing && (
          <div className="live-missing">
            <span className="live-missing-text">
              ⚠ Keine aktuelle Live-Ansicht — du siehst nur ältere History, <strong>nicht den aktuellen Stand</strong>.
            </span>
            <button type="button" className="live-missing-btn" onClick={() => window.location.reload()}>
              <RefreshCw size={13} /> Neu laden
            </button>
          </div>
        )}

        {/* Scroll-up affordance — older transcript turns above the live view. */}
        {state !== 'suspended' && hist.hasMore && (
          <div className="history-top">
            {hist.loadingOlder
              ? <span className="history-top-loading"><span className="cmd-spinner" /> Lade ältere…</span>
              : <button type="button" className="history-top-more" onClick={hist.loadOlder}>Ältere laden</button>}
          </div>
        )}

        {state !== 'suspended' && displayTurns
          .filter((turn) => turnVisibleInFilter(streamFilter, {
            hasUser: Boolean((turn as { user?: string }).user),
            // „Eigen" ist dieselbe Unterscheidung, an der auch der Sprung und
            // die Anzeige hängen: kein [IACP:…]- und kein [Queue …]-Umschlag.
            isOwn: !parseTurnMeta(turn).sender && Boolean((turn as { user?: string }).user),
          }))
          .map((turn, idx) => (
          <TurnEntry
            key={(turn as { uuid?: string }).uuid || `v2-${turn.id}`}
            anchorKey={(turn as { uuid?: string }).uuid || `v2-${turn.id}`}
            turn={turn}
            onSelectSession={onSelectSession}
            // Only the LAST turn is "in flight" while the agent works — hide
            // its recap / task-list / reply-actions until it's done. Gate on
            // the REAL working state only (agentBusy/sending), NOT busyHold —
            // busyHold lingers up to 12 s after the turn ends, which kept the
            // 🔊/⧉ reply actions hidden long after the answer was done (Alex
            // 2026-06-23: "ich sehe keine tts Button").
            hideTrailing={idx === (displayTurns.length - 1) && (agentBusy || sending) && !stopped}
            userOnly={streamFilter !== 'all'}
          />
        ))}

        {/* data LOADED but empty (no live turns + no history) → not the initial-load
            "Loading {name}…" below (that covers data===null), but the "loaded blank" case
            that used to be a silent white screen (Alex 2026-07-02). Self-heals via polling
            (empty results no longer hash-cache); the reload is a manual escape hatch. */}
        {state !== 'suspended' && data !== null && displayTurns.length === 0 && (
          <div className="empty-view">
            <AgentOsWave size="md" />
            <span className="empty-view-text">Keine aktuelle Ansicht — wird geladen…</span>
            <button type="button" className="empty-view-btn" onClick={() => window.location.reload()}>
              <RefreshCw size={13} /> Neu laden
            </button>
          </div>
        )}

        {data?.step_progress && data.step_progress.steps.length > 0 && (
          <StepProgressBar
            sessionName={sessionName}
            data={data.step_progress}
          />
        )}

        {/* Permission gate takes priority over multi_choice — it's a
            blocking approval, not a menu (Cloud IACP 0d45d2ef). */}
        {data?.permission && data.permission.options.length > 0 && (
          <PermissionBlock
            sessionName={sessionName}
            data={data.permission}
          />
        )}

        {data?.multi_choice && (
          <MultiChoiceBlock
            sessionName={sessionName}
            data={data.multi_choice}
          />
        )}

        {data?.input_box && (data.input_box.text || data.input_box.suggestion) && (
          <SuggestionBar
            sessionName={sessionName}
            data={data.input_box}
          />
        )}

        {(sending || agentBusy || busyHold) && !stopped && !isBlocked && (
          <div className="sending-indicator" aria-label="agent thinking">
            {/* Stop sits here, left of the "working" wave — that's where you
                SEE it working, so that's where you stop it (Alex 2026-06-20,
                moved out of the input row). */}
            <button className="sending-stop" onClick={stopAgent} title="Agent stoppen">
              <Square size={13} />
            </button>
            {/* Sound-wave (same animation as the transcribe state) instead
                of the old three bouncing dots — Alex 2026-06-20 found the
                dots fad; the flowing wave reads as "lebendig / arbeitet". */}
            <span className="sending-wave" aria-hidden>
              {[0, 1, 2, 3, 4].map((i) => (
                <span
                  key={i}
                  className="sending-wave-bar"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </span>
            {/* Inline live-status next to the dots (Alex 2026-06-18: the
                bare bouncing dots leave you unsure if anything's happening
                — show WHAT he's working on + the next step right here at
                the bottom where you wait for the reply). Falls back to the
                plain dots when no live_status is captured this turn. The
                top Now/Next card stays for when you're scrolled up. */}
            {liveStatus && (
              <span className="sending-live">
                <span className="sending-live-act">{liveStatus.activity || 'arbeitet…'}</span>
                {Number.isFinite(liveStatus.started_at) && (
                  <span className="sending-live-elapsed"> · {Math.max(0, Math.round(now - liveStatus.started_at))}s</span>
                )}
                {liveStatus.next && (
                  <span className="sending-live-next">→ {liveStatus.next}</span>
                )}
              </span>
            )}
          </div>
        )}

        {!data && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '64px 16px',
              color: 'var(--text-muted)',
              fontSize: 13,
              gap: 8,
            }}
          >
            <AgentOsWave size="lg" label={`Loading ${sessionName}…`} />
          </div>
        )}
        </div>
      </div>
        {showScrollDown && (
          <button className="scroll-down-btn" onClick={scrollToBottom} aria-label="Nach unten scrollen" title="Zum aktuellen Stand">
            <ChevronDown size={18} />
          </button>
        )}
      </div>

      <div className="agent-footer" ref={glassFooterRef}>
        {/* Über dem Eingabefeld statt oben im Verlauf (Alex, 12.08.): Dort
            steht der Blick ohnehin, und oben scrollt der Hinweis beim Lesen
            aus dem Bild — ausgerechnet der Hinweis, der erklärt, warum das
            Bild unvollständig aussieht. */}
        {streamFilter !== 'all' && (
          <div className="stream-filter-note">
            {STREAM_FILTER_LABEL[streamFilter]} — Antworten ausgeblendet · <b>Alt+F</b> weiter
          </div>
        )}
        <InputBar
          ref={inputBarRef}
          sessionName={sessionName}
          onSending={handleSending}
          agentBusy={agentBusy}
          hideTts
          attachLeft
        />
      </div>
    </div>
  );
}
